import { GetServerSidePropsContext, GetServerSidePropsResult } from "next"
import { NextRouter } from "next/router"
import { MessageRef } from "@/types/chat"
import { CollectionRef } from "@/types/collection"
import { DayObject } from "@/types/date"
import { Entity, EntityRef } from "@/types/entity"
import { Organization } from "@/types/organization"
import { TravelerGroup, TripRef } from "@/types/trip"
import { CurrentUser, UserRef } from "@/types/user"
import { Honeybadger } from "@honeybadger-io/react"
import { AxiosInstance } from "axios"

import { CreatorApi } from "@/lib/creator-api"
import { isDevelopment } from "@/lib/utils/misc"
import { present } from "@/lib/utils/presence"
import { singleParam, validateMarketingParams } from "@/lib/utils/url"
import {
  addParamsToUrl,
  addParamToUrl,
  hasParams,
  rewriteUrlWithHostAndProtocol,
} from "@/lib/utils/url-params"
import {
  addOrganizationHostToUrl,
  entityIsGuide,
  entityIsPlace,
  isEvent,
  isHotel,
} from "./entity-utils"
import { isClientSide } from "./is-client-side"
import { mtLocation } from "./mt-location"
import { getDefaultEventParams } from "./place-utils"

export interface HotelSearchParams {
  start?: DayObject | null
  end?: DayObject | null
  travelers?: TravelerGroup | null
}

export const computeShareUrl = ({
  user,
  entity,
  organization,
  canPublishVanityLink,
  router,
  hotelSearchParams,
}: {
  user: CurrentUser
  entity: Entity
  organization: Organization | undefined
  canPublishVanityLink: boolean
  router?: NextRouter
  hotelSearchParams?: HotelSearchParams
}) => {
  let url = entity.canonical_url
  if (present(url) && canAddRefId({ user, entity, canPublishVanityLink })) {
    url = addParamToUrl(url, "refId", user.user_ref)
  }
  if (present(url) && entityIsPlace(entity) && isEvent(entity)) {
    const at =
      router?.query.at?.toString() ||
      getDefaultEventParams(entity)?.at?.toString()
    if (present(at)) {
      url = addParamToUrl(url, "at", at)
    }
  }
  if (present(url) && entityIsPlace(entity) && isHotel(entity)) {
    const start =
      router?.query.start?.toString() ||
      (hotelSearchParams?.start
        ? `${hotelSearchParams.start.year}-${String(hotelSearchParams.start.month).padStart(2, "0")}-${String(hotelSearchParams.start.day).padStart(2, "0")}`
        : undefined)
    const end =
      router?.query.end?.toString() ||
      (hotelSearchParams?.end
        ? `${hotelSearchParams.end.year}-${String(hotelSearchParams.end.month).padStart(2, "0")}-${String(hotelSearchParams.end.day).padStart(2, "0")}`
        : undefined)
    const adults =
      router?.query.adults?.toString() ||
      (hotelSearchParams?.travelers?.adult_count
        ? String(hotelSearchParams.travelers.adult_count)
        : undefined)
    const children =
      router?.query.children?.toString() ||
      (hotelSearchParams?.travelers?.child_count
        ? String(hotelSearchParams.travelers.child_count)
        : undefined)
    if (present(start)) url = addParamToUrl(url, "start", start)
    if (present(end)) url = addParamToUrl(url, "end", end)
    if (present(adults)) url = addParamToUrl(url, "adults", adults)
    if (present(children)) url = addParamToUrl(url, "children", children)
  }
  if (organization) {
    url = addOrganizationHostToUrl(url, organization)
  }

  // For convenient of local testig so we don't always get https://mindtrip.ai... (the canonical form)
  return isDevelopment() && isClientSide() && !organization
    ? rewriteUrlWithHostAndProtocol(mtLocation.href, url)
    : url
}

export const canAddRefId = ({
  user,
  entity,
  canPublishVanityLink,
}: {
  user: CurrentUser
  entity: Entity
  canPublishVanityLink: boolean
}) => {
  if (canPublishVanityLink) {
    if (entityIsGuide(entity)) {
      // Inspiration that you own
      if (entity.owner.user_ref === user.user_ref) {
        return true
      }
    } else {
      // place
      return true
    }
  }
  return false
}

export async function handleCreatorAttributionRedirect(
  context: GetServerSidePropsContext,
  serverAxios: AxiosInstance,
  objectRef?: EntityRef | MessageRef | TripRef | CollectionRef
): Promise<GetServerSidePropsResult<never> | null> {
  const userRef = singleParam<UserRef>(context.query["refId"])
  if (!present(userRef)) return null
  const { req } = context
  const protocol = req.headers["x-forwarded-proto"] || "http"
  const clientUrl = `${protocol}://${req.headers.host}${req.url}`
  const creatorApi = new CreatorApi(serverAxios)
  const ret = await creatorApi.attributeToPartner(userRef, objectRef)

  if (ret.status !== "success") {
    // If something went wrong, no need to fail for the attribution nor
    // show any error as there is nothing the user can do
    Honeybadger.logger.warn(
      "Unable to attribute: ",
      clientUrl,
      " message: ",
      ret.msg
    )
    return null
  }

  let newUrl = ret.url

  if (isDevelopment()) {
    newUrl = rewriteUrlWithHostAndProtocol(clientUrl, newUrl)
  }

  // preserve the 'at' query param for events if it was in the original URL
  // (this ensures the user sees the specific event instance they requested)
  const atParam = singleParam(context.query["at"])
  if (present(atParam)) {
    newUrl = addParamToUrl(newUrl, "at", atParam)
  }
  // Preserve the 't' query param for view-only collection link
  const tParam = singleParam(context.query["t"])
  if (present(tParam)) {
    newUrl = addParamToUrl(newUrl, "t", tParam)
  }

  if (!hasParams(clientUrl, "utm_")) {
    const marketingParams = {
      utm_campaign: "creator_refer_pages",
      utm_source: "creator",
      utm_medium: "social",
      utm_partner: userRef,
    }
    validateMarketingParams(marketingParams, "creator attribution redirect")
    newUrl = addParamsToUrl(newUrl, marketingParams)
  }

  return {
    redirect: {
      destination: newUrl,
      permanent: false,
    },
  }
}
